/**
 * player_clips.ts — the PLAYER clip grammar (player wave 5, 2026-10-08). PURE (no three, no DOM): what a traveler's
 * rigged body plays for what the game knows about it. `verify:player-clips` pins every rule below.
 *
 * TWO GRAMMARS, CHOSEN BY THE RIG THAT IS WORN.
 *   · LEGACY (R8, 2026-09-25) — `playerClipLegacy`, moved here VERBATIM from versona_rig.ts: swim / sit / lie / ride /
 *     fly / Walking / Running / Idle. A rig that lacks the wave-5 clips (every `avatar-rig:player_*` cooked before the
 *     swap: the donor skeleton + 77 clips) is driven by it and by nothing else — `stepPlayerAnim` returns its answer
 *     untouched, no body turn, the crouch squash still on. THE HAS LAW: a clip plays only if the worn rig HAS it.
 *   · WAVE 5 — a rig that carries the twenty player clips (tools/asset-pipeline/player-wave5-clips.json; the mark is
 *     `Swim_Idle`): ground-speed matching, the body turned toward its travel, crouch by clip, swim, the air, slide,
 *     roll, vault, hit, guard, sit-down, and (pure + bed only until something feeds them) the jabs and the knock-down.
 *
 * GROUND-SPEED MATCHING (nobody skates). A walk mode of 6 m/s is a RUN and a sprint is 9: the R8 grammar played
 * `Walking` (a 1.5 m/s stride) under my own body at 6 m/s. Each locomotion clip has a NATURAL ground speed — how fast
 * the hips pass over the planted toe, measured from the clip itself on a 1.90 m body (tools/asset-pipeline/out-player5/
 * measure-stride.mjs) — and the clip's time scale is the body's real speed over that, clamped to what a leg can do.
 *   MEASURED AND RULED OUT: `Lean_Forward_Sprint_inplace` strides at 3.9–4.3 m/s — SLOWER than the rig's own `Running`
 *   (4.9). At 9 m/s it would need ×2.1 (seven steps a second); `Running` needs ×1.83. Sprint therefore runs on
 *   `Running`; the lean clip is on every wave-5 rig and is not used for locomotion.
 *
 * THE BODY TURNS TOWARD ITS TRAVEL. The library has no strafe and no fast back-pedal: a standing body moving sideways
 * or backwards turns to face where it is going (the camera / the aim keeps the row's yaw); only a SLOW backward move
 * walks backwards. Crouched, the four directional crouch clips are used instead and the body does not turn.
 *
 * NEVER MIME: nothing here names a gun, blade, torch or finger clip. A jab needs `strike` (bare hands — the caller's
 * word that no weapon is in the hand); a seat clip plays on a real seat (`sit` is functional furniture, `ride:` a hull).
 */
import { clipFor, type VersonaClip } from './versona_clips.js';

/** The twenty clips of the player wave (names = the files in out-player5/newclips, = Meshy's action names). */
export const PLAYER_WAVE5_CLIPS = [
  'Swim_Idle', 'Swim_Forward',
  'Cautious_Crouch_Walk_Forward_inplace', 'Cautious_Crouch_Walk_Backward_inplace', 'Cautious_Crouch_Walk_Left_inplace', 'Cautious_Crouch_Walk_Right_inplace',
  'CrouchLookAroundBow', 'Lean_Forward_Sprint_inplace', 'Walk_Backward_inplace', 'Jump_Run', 'Fall2', 'slide_right', 'Roll_Dodge',
  'Unarmed_Vault', 'Arise', 'dying_backwards', 'Hit_Reaction_1', 'Right_Jab_from_Guard', 'Left_Jab_from_Guard', 'Stand_to_Sit_Transition_M',
] as const;
export type PlayerWave5Clip = (typeof PLAYER_WAVE5_CLIPS)[number];
/** A rig that has this clip is a wave-5 player rig (the whole set is cooked together). */
export const PLAYER_WAVE5_MARK: PlayerWave5Clip = 'Swim_Idle';

/** THE LEGACY GRAMMAR (R8) — verbatim. The clip a PLAYER plays from their row's animState + motion. */
export function playerClipLegacy(animState: string, moving: boolean, sprinting: boolean): { clip: VersonaClip; loop: boolean; speed: number } {
  if (animState === 'swim') return clipFor('idle', { swimming: true, moving, phase: 0 });
  if (animState === 'sit' || animState.startsWith('ride:')) return clipFor('sitting', { moving: false, phase: 0, seat: 'chair' });
  if (animState === 'lie') return clipFor('convalescing', { moving: false, phase: 0, seat: 'cot' });
  if (animState.startsWith('fly:')) return clipFor('sitting', { moving: false, phase: 0, seat: 'chair' });
  if (moving) return { clip: sprinting ? 'Running' : 'Walking', loop: true, speed: 1 };
  return { clip: 'Idle', loop: true, speed: 1 };
}

/** Natural ground speed (m/s) of each locomotion clip on a 1.90 m body — the hips over the planted toe (measured). */
export const PLAYER_STRIDE_MPS: Readonly<Record<string, number>> = {
  Walking: 1.54, Running: 4.93, Walk_Backward_inplace: 1.44,
  Cautious_Crouch_Walk_Forward_inplace: 1.40, Cautious_Crouch_Walk_Backward_inplace: 1.20,
  Cautious_Crouch_Walk_Left_inplace: 1.25, Cautious_Crouch_Walk_Right_inplace: 1.20,
};
/** What a leg can do: a clip is never played slower / faster than this (past it the feet slide a little instead). */
export const STRIDE_SCALE_MIN = 0.55, STRIDE_SCALE_MAX = 1.9;
/** Under this ground speed a standing body WALKS (and may walk backwards); above it, it runs. */
export const PLAYER_WALK_MAX_MPS = 2.75;
/** The time scale that makes `clip` cover the ground at `speedMps` (1 for a clip with no measured stride). */
export function strideScale(clip: string, speedMps: number): number {
  const nat = PLAYER_STRIDE_MPS[clip];
  if (!nat || !Number.isFinite(speedMps)) return 1;
  return Math.min(STRIDE_SCALE_MAX, Math.max(STRIDE_SCALE_MIN, speedMps / nat));
}
/** How far the feet slide at that speed, as a share of the ground covered (0 = planted). */
export function strideSkate(clip: string, speedMps: number): number {
  const nat = PLAYER_STRIDE_MPS[clip];
  if (!nat || !(speedMps > 0)) return 0;
  return Math.abs(speedMps - strideScale(clip, speedMps) * nat) / speedMps;
}

export const wrapPi = (a: number): number => { let x = a % (Math.PI * 2); if (x > Math.PI) x -= Math.PI * 2; if (x < -Math.PI) x += Math.PI * 2; return x; };
/**
 * Where the body travels RELATIVE TO ITS FACING, radians: 0 = forward, + = to its LEFT, ±π = backward. A player's
 * forward is (−sin yaw, −cos yaw) (combat_core), so a world velocity (vx, vz) travels at yaw atan2(−vx, −vz).
 */
export function travelOffset(vx: number, vz: number, yaw: number): number { return wrapPi(Math.atan2(-vx, -vz) - yaw); }

/** What the game knows about one body this frame. */
export interface PlayerAnimInput {
  /** The row's state (or the local equivalent): idle · walk · sprint · crouch · slide · dash · swim · sit · lie · jump · ride:x · fly:x. */
  animState: string;
  moving: boolean;
  sprinting: boolean;
  /** Ground speed, m/s. */
  speed: number;
  /** `travelOffset`, or null when it is not known / the body is still. */
  travel: number | null;
  /** Off the ground (my own body only: on-foot height is client-only, a remote row carries no Y). */
  airborne?: boolean;
  mantling?: boolean;
  /** Staggered by a blow. */
  hurt?: boolean;
  /** The guard is raised. */
  guard?: boolean;
  /** A BARE-HAND strike began this frame (never set with a weapon in the hand — none is drawn). */
  strike?: boolean;
  /** Knocked down / getting up (nothing feeds this yet — see PHASE2.md). */
  downed?: 'down' | 'rising' | null;
}
/** What the rig layer does with it. */
export interface PlayerPose {
  clip: string; loop: boolean; speed: number;
  /** Seconds into the clip to start at (skips an anticipation the game's instant jump has no time for). */
  startAt: number;
  /** The body's turn away from the row's yaw, radians (0 = faces where it looks). */
  bodyYaw: number;
  /** The clip is a one-shot the grammar is holding (no foot IK under it). */
  latched: boolean;
  /** 'legacy' = the R8 grammar answered (the rig has no wave-5 clips). */
  grammar: 'legacy' | 'wave5';
}
/** Per-body memory between frames. */
export interface PlayerAnimMemo { latch: PlayerPose | null; latchLeft: number; prev: string; prevHurt: boolean; prevMantle: boolean; prevDowned: string; airT: number; jab: number }
export function newPlayerAnimMemo(): PlayerAnimMemo { return { latch: null, latchLeft: 0, prev: '', prevHurt: false, prevMantle: false, prevDowned: '', airT: 0, jab: 0 }; }

/** One-shots: the clip, how fast it plays, how long the grammar holds it (s), and where it starts. */
export const PLAYER_ONE_SHOTS = {
  sitDown: { clip: 'Stand_to_Sit_Transition_M', speed: 2.0, hold: 2.3, startAt: 0 },   // 4.77 s clip → 2.4 s
  slide: { clip: 'slide_right', speed: 1.95, hold: 0.9, startAt: 0 },                  // 1.77 s clip over the 0.9 s slide
  dash: { clip: 'Roll_Dodge', speed: 3.0, hold: 0.6, startAt: 0 },                     // 1.87 s roll inside the 0.9 s dash cadence
  vault: { clip: 'Unarmed_Vault', speed: 1.7, hold: 0.5, startAt: 0 },                 // 0.87 s over the 0.5 s mantle
  hurt: { clip: 'Hit_Reaction_1', speed: 1.6, hold: 0.75, startAt: 0 },                // 1.23 s over the 0.6 s stagger
  arise: { clip: 'Arise', speed: 1.0, hold: 1.85, startAt: 0 },
  jabRight: { clip: 'Right_Jab_from_Guard', speed: 2.4, hold: 0.8, startAt: 0 },       // the 800 ms bare-hand cadence
  jabLeft: { clip: 'Left_Jab_from_Guard', speed: 2.2, hold: 0.8, startAt: 0 },
  hop: { clip: 'Regular_Jump', speed: 1.5, hold: 1.25, startAt: 0.3 },                 // a standing jump: 1.27 s in the air
} as const;
/** After this long off the ground a jump has become a fall. */
export const PLAYER_FALL_AFTER_S = 1.35;
/** Shorter than this off the ground is a step or a bump, not a jump. */
export const PLAYER_AIR_MIN_S = 0.1;

const shot = (k: keyof typeof PLAYER_ONE_SHOTS, bodyYaw = 0): PlayerPose => { const s = PLAYER_ONE_SHOTS[k]; return { clip: s.clip, loop: false, speed: s.speed, startAt: s.startAt, bodyYaw, latched: true, grammar: 'wave5' }; };
const loopOf = (clip: string, speed = 1, bodyYaw = 0): PlayerPose => ({ clip, loop: true, speed, startAt: 0, bodyYaw, latched: false, grammar: 'wave5' });

/**
 * One frame of the grammar. `has(clip)` = the worn rig carries it. Mutates `memo`. A rig without the wave-5 mark gets
 * the LEGACY answer and the memo is left alone.
 */
export function stepPlayerAnim(memo: PlayerAnimMemo, i: PlayerAnimInput, dt: number, has: (clip: string) => boolean): PlayerPose {
  const legacy = (): PlayerPose => ({ ...playerClipLegacy(i.animState, i.moving, i.sprinting), startAt: 0, bodyYaw: 0, latched: false, grammar: 'legacy' });
  if (!has(PLAYER_WAVE5_MARK)) return legacy();
  const s = i.animState, step = Math.max(0, Number.isFinite(dt) ? dt : 0);
  const entered = s !== memo.prev, wasHurt = memo.prevHurt, wasMantling = memo.prevMantle, wasDowned = memo.prevDowned;
  memo.prev = s; memo.prevHurt = !!i.hurt; memo.prevMantle = !!i.mantling; memo.prevDowned = i.downed ?? '';
  memo.airT = i.airborne ? memo.airT + step : 0;
  if (memo.latchLeft > 0) memo.latchLeft -= step;
  const hold = (k: keyof typeof PLAYER_ONE_SHOTS, bodyYaw = 0): PlayerPose => {
    const p = shot(k, bodyYaw);
    if (!has(p.clip)) return fallback();
    memo.latch = p; memo.latchLeft = PLAYER_ONE_SHOTS[k].hold; return p;
  };
  const drop = (): void => { memo.latch = null; memo.latchLeft = 0; };
  /** The wave-5 answer with one clip missing = the legacy answer (THE HAS LAW). */
  const fallback = (): PlayerPose => ({ ...legacy(), grammar: 'wave5' });
  const pick = (p: PlayerPose): PlayerPose => (has(p.clip) ? p : fallback());
  const seated = s === 'sit' || s === 'lie' || s.startsWith('ride:') || s.startsWith('fly:');

  // ── the things that interrupt anything ──
  if (i.downed === 'down') { drop(); return pick({ clip: 'dying_backwards', loop: false, speed: 1, startAt: 0, bodyYaw: 0, latched: true, grammar: 'wave5' }); }
  if (i.downed === 'rising' && wasDowned !== 'rising') return hold('arise');
  if (i.hurt && !wasHurt && !seated && s !== 'swim') return hold('hurt');
  // ── a held one-shot runs to its end, unless the body has gone somewhere it cannot be played ──
  if (memo.latch && memo.latchLeft > 0) {
    const sitting = memo.latch.clip === PLAYER_ONE_SHOTS.sitDown.clip;
    if (sitting ? s === 'sit' : !seated && s !== 'swim') return memo.latch;
    drop();
  } else if (memo.latch) drop();

  // ── seats and hulls ──
  if (s === 'sit') return entered ? hold('sitDown') : fallback();
  if (seated) return fallback();
  // ── water ──
  if (s === 'swim') return pick(i.moving ? loopOf('Swim_Forward', Math.min(1.4, Math.max(0.6, i.speed / 2.4))) : loopOf('Swim_Idle'));
  // ── the climb, the air ──
  if (i.mantling) return wasMantling ? pick(shot('vault')) : hold('vault'); // held from the moment the climb starts, never re-armed while it lasts
  if (i.airborne && memo.airT > PLAYER_AIR_MIN_S) {
    if (memo.airT > PLAYER_FALL_AFTER_S) return pick(loopOf('Fall2'));
    return pick(i.moving ? { clip: 'Jump_Run', loop: false, speed: 1.6, startAt: 0.25, bodyYaw: i.travel ?? 0, latched: true, grammar: 'wave5' }
      : { clip: 'Regular_Jump', loop: false, speed: PLAYER_ONE_SHOTS.hop.speed, startAt: PLAYER_ONE_SHOTS.hop.startAt, bodyYaw: 0, latched: true, grammar: 'wave5' });
  }
  if (s === 'jump' && entered) return hold('hop');             // a remote's standing jump (the row says so; its height does not travel)
  // ── the ground moves ──
  if (s === 'slide' && entered) return hold('slide', i.travel ?? 0);
  if (s === 'dash' && entered) return hold('dash', i.travel ?? 0);
  if (i.strike && !i.moving && s !== 'crouch') { memo.jab++; return hold(memo.jab % 2 ? 'jabRight' : 'jabLeft'); }
  if (s === 'crouch') {
    if (!i.moving) return pick(loopOf('CrouchLookAroundBow'));
    const t = i.travel ?? 0, a = Math.abs(t);
    const clip = a <= Math.PI / 4 ? 'Cautious_Crouch_Walk_Forward_inplace' : a >= (3 * Math.PI) / 4 ? 'Cautious_Crouch_Walk_Backward_inplace'
      : t > 0 ? 'Cautious_Crouch_Walk_Left_inplace' : 'Cautious_Crouch_Walk_Right_inplace';
    return pick(loopOf(clip, strideScale(clip, i.speed)));
  }
  if (i.moving) {
    const t = i.travel ?? 0;
    if (i.speed < PLAYER_WALK_MAX_MPS) {
      if (Math.abs(t) > (3 * Math.PI) / 4 && has('Walk_Backward_inplace')) return loopOf('Walk_Backward_inplace', strideScale('Walk_Backward_inplace', i.speed));
      return pick(loopOf('Walking', strideScale('Walking', i.speed), t));
    }
    return pick(loopOf('Running', strideScale('Running', i.speed), t));
  }
  if (i.guard) return pick(loopOf('Block1'));
  return fallback();
}

/**
 * THE SEAT — where a clip must put the body for the game's own geometry to be true (measured on the cooked candidate:
 * tools/asset-pipeline/out-player5/measure-pose.mjs, a 1.90 m body whose head joint rests at 1.637 m and whose crown
 * is 0.246 m above it). A LIFT only — a translation of the rig root.
 *   · SWIM. A swimmer's feet-origin is held 1.15 m under the surface. Swim_Forward lies with its hips 0.45 m above
 *     that origin — 0.70 m under water, where the lying stand-in it replaces floated AT the surface: it is lifted so
 *     the hips ride 0.18 m under and the head breaks the surface. Swim_Idle treads upright with the head joint 0.31 m
 *     above the surface (the chest out of the water): it is lowered to the neck.
 * `lift` is metres of a 1.90 m body (the caller scales it with the rig).
 *
 * THE SHEAR LAW — NEVER A NON-UNIFORM SCALE ON A RIG OR ON ANYTHING ABOVE IT. On the tiers with `dqSkinning` (high,
 * ultra — the default) a rig is skinned with dual quaternions, and a dual quaternion carries a rotation, a translation
 * and ONE scale: under a non-uniform ancestor scale a rotated bone's world matrix is sheared and the quaternion read
 * from it is garbage. This file once gave the four crouch walks a residual squash (scale.y 0.85) to keep the crown
 * under the old 0.72 squash's 1.37 m; the bed's numbers passed and its picture was a body whose legs ran out into
 * ribbons along the ground. So the crouch WALK is drawn as authored: crown 1.58 m, against 1.36 m for the still
 * crouch and a 1.1 m crouched capsule — a crouch-walking body seen from outside clips a 1.4 m slab by its head. A
 * lower crouch walk is a clip to buy, not a scale to apply. (The R8 rigs' crouch IS still a 0.72 scale on their
 * proxy — AtlasAvatarModule CROUCH_BODY_SCALE — and shears the same way on those tiers: the bed draws it.)
 */
export const PLAYER_SWIM_FORWARD_LIFT_M = 0.52;
export const PLAYER_SWIM_IDLE_LIFT_M = -0.25;
export function playerPoseSeat(clip: string): { lift: number } {
  if (clip === 'Swim_Forward') return { lift: PLAYER_SWIM_FORWARD_LIFT_M };
  if (clip === 'Swim_Idle') return { lift: PLAYER_SWIM_IDLE_LIFT_M };
  return { lift: 0 };
}

/**
 * THE SKIN KEY FOLLOWS THE RIG'S CONTENT. The skin table (lib/skin_shading.ts SKIN_BODY_TABLE) is measured on each
 * SERVED rig's own texture, and one body key (`avatar:player_<name>`) names two generations of rig across the swap —
 * and both at once under the `atlas:player-bodies` switch. A rig that carries the wave-5 clips reads the
 * `player5_<name>` entry under whatever key it is registered (`player5_`, `player5lite_`, and after the swap
 * `player_` / `playerlite_`); any other player rig reads `player_<name>` (the R8 rigs: `player_` today, `player4_`
 * after). A key that is not a player body's is returned as it came.
 */
export function playerSkinKey(key: string, wave5: boolean): string {
  const m = /^(?:avatar(?:-rig)?:)?player(?:4|5)?(?:lite)?_([a-z0-9]+)$/.exec(key);
  return m ? `${wave5 ? 'player5_' : 'player_'}${m[1]}` : key;
}

/**
 * THE LIGHT ROW IS IN FORCE ONLY BESIDE ITS OWN FULL RIG. Two rigs of one body are interchangeable only when they are
 * two assets drawn at one scale: a body that is re-ranked must not change height or generation. The case this guards
 * is the swap's --undo — the server cannot remove the light key (`set_prefab_model … 0` is refused), so after an undo
 * `avatar-rig:playerlite_<name>` still names the NEW light rig (scale 1) beside the restored OLD rig (scale ≈ 1.084):
 * that pair is refused here and the body wears its one full rig for everything, as it did before the swap.
 */
export function lightRigInForce(full: { assetId: bigint; defaultScale: number } | null, light: { assetId: bigint; defaultScale: number } | null): boolean {
  return !!full && !!light && light.assetId !== full.assetId && Math.abs(light.defaultScale - full.defaultScale) <= 1e-3;
}

/**
 * THE STATIC LEVEL. The static twin is what a tier that wears no rig draws for a player (minimal), and the fallback
 * when a rig fails. It is loaded at LOD2 only on the tiers whose far bodies are LOD2 (`farBodyLod` 2) AND only when
 * the twin's LOD2 is a real level — at most 30 % of its LOD0, the cooker's own LEVEL LAW. The wave-5 twins qualify
 * (28k → ≈4.5k); the scans they replace do not (nineteen "LOD2"s are their LOD0 over again, one is 49 %), so every
 * body before the swap loads exactly as it always did.
 */
export const PLAYER_STATIC_LOD2_MAX_SHARE = 0.3;
export function playerStaticLod(farBodyLod: number, variants: ReadonlyArray<{ lod: number; polyCount: number }>): 0 | 2 {
  if (!(farBodyLod >= 2)) return 0;
  const l0 = variants.find((v) => v.lod === 0), l2 = variants.find((v) => v.lod === 2);
  return l0 && l2 && l2.polyCount > 0 && l2.polyCount <= l0.polyCount * PLAYER_STATIC_LOD2_MAX_SHARE ? 2 : 0;
}

/**
 * WHICH RIG A PLAYER BODY WEARS (the budget). Two rigs exist per wave-5 body: the FULL one (~298k triangles, ~43 MB)
 * and the LIGHT one (≤ 30k, today's class of body). The full rig is for the body nearest the eye on the tiers that
 * draw full versona bodies (`farBodyLod` 0 = high / ultra): my own third-person body and the nearest few remote
 * players. Everyone else, on every tier, wears the light rig — so no player stops animating anywhere a player animates
 * today, and no tier and no crowd of players gets heavier than it can carry. A body with no light rig registered (every
 * body before the swap) wears its one rig, whatever this says.
 */
export function playerRigPlan(preset: { playerBodyRig?: boolean; farBodyLod?: number; nearActors?: number }): { rig: boolean; localFull: boolean; fullRemotes: number } {
  const rig = preset.playerBodyRig === true;
  const full = rig && (preset.farBodyLod ?? 0) === 0;
  return { rig, localFull: full, fullRemotes: full ? Math.min(4, Math.max(0, Math.round((preset.nearActors ?? 0) / 6))) : 0 };
}
